import { ConfirmDialog, notify } from "@/components/kit";
import { useDeleteBanner } from "@/hooks/api/banner/useDeleteBanner";
import { Banner } from "@/types";
import { getApiErrorMessage } from "@/utils/api-error";

interface DeleteBannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  banner: Banner | null;
}

export function DeleteBannerModal({
  isOpen,
  onClose,
  banner,
}: DeleteBannerModalProps) {
  const { mutate: deleteBanner, isPending: isDeletingBanner } =
    useDeleteBanner();

  const handleDelete = () => {
    if (!banner) return;

    deleteBanner(banner.id, {
      onSuccess: () => {
        notify.success("Banner deleted");
        onClose();
      },
      onError: (error) => {
        notify.error(
          "Failed to delete banner",
          getApiErrorMessage(error, "Please try again"),
        );
      },
    });
  };

  return (
    <ConfirmDialog
      confirmLabel="Delete"
      isOpen={isOpen}
      isPending={isDeletingBanner}
      title="Delete banner"
      onClose={onClose}
      onConfirm={handleDelete}
    >
      <div className="flex flex-col gap-3">
        <p>
          You&apos;re about to delete this banner. It will no longer show in the
          app.
        </p>
        {banner ? (
          <div className="bg-surface-secondary flex items-center gap-3 rounded-2xl p-3">
            <span className="bg-surface block aspect-[2/1] w-32 shrink-0 overflow-hidden rounded-xl">
              <img
                alt="Banner"
                className="size-full object-cover"
                src={banner.imageUrl}
              />
            </span>
            <span className="text-muted min-w-0 font-mono text-xs break-all">
              {banner.link}
            </span>
          </div>
        ) : null}
      </div>
    </ConfirmDialog>
  );
}
